import { useTranslation } from 'react-i18next';
import { anunciarOcultos } from '../sensivel';
import { data, dinheiro } from '../../nucleo/formato';
import type { PontoSerie } from './tipos';

/**
 * Fase 2 (W1, E8): fluxo de caixa com barras de entrada e saída e a linha do saldo ACUMULADO. O projeto não tem
 * biblioteca de gráfico: SVG simples com os tokens (verde, perigo e o apoio Blue Green na linha de saldo), cor chapada. Os rótulos ficam em HTML (o SVG
 * estica na largura), e uma tabela só para leitor de tela traz os números. Fase 2 (W21, K7): de hoje em diante, o saldo
 * PREVISTO (saldo + títulos em aberto do Contas por vencimento) em linha tracejada da mesma cor, com legenda própria.
 */
export function GraficoFluxo({ pontos, moeda, granularidade }: { pontos: PontoSerie[]; moeda: string; granularidade: 'dia' | 'semana' }) {
  const { t } = useTranslation();
  const n = Math.max(1, pontos.length);
  const larg = n * 10;
  const prev = (p: PontoSerie) => (typeof p.previsto === 'number' ? p.previsto : null);
  const temPrevisto = pontos.some((p) => prev(p) !== null);
  const maximo = Math.max(1, ...pontos.flatMap((p) => [p.entradas, p.saidas, p.saldo, prev(p) ?? 0]));
  const minimo = Math.min(0, ...pontos.map((p) => Math.min(p.saldo, prev(p) ?? 0)));
  const faixa = maximo - minimo || 1;
  const y = (v: number) => 100 - ((v - minimo) / faixa) * 100;
  const zero = y(0);
  const linha = pontos.map((p, k) => `${k === 0 ? 'M' : 'L'}${k * 10 + 5},${y(p.saldo).toFixed(2)}`).join(' ');
  const comPrevisto = pontos.map((p, k) => ({ k, v: prev(p) })).filter((x): x is { k: number; v: number } => x.v !== null);
  const linhaPrevista = comPrevisto.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.k * 10 + 5},${y(x.v).toFixed(2)}`).join(' ');
  const ult = pontos.at(-1);
  const rotuloData = (d: string) => (granularidade === 'semana' ? t('inicio.grafico.semana', { data: data(d) }) : data(d));
  return (
    <figure className="min-w-0">
      <div className="flex justify-between gap-2 text-xs text-tinta-3">
        <span className="numero">{anunciarOcultos(dinheiro(maximo, moeda))}</span>
      </div>
      <svg
        viewBox={`0 0 ${larg} 100`}
        preserveAspectRatio="none"
        className="mt-1 h-40 w-full overflow-visible"
        role="img"
        aria-label={t('inicio.grafico.aria', {
          de: pontos[0] ? data(pontos[0].data) : '',
          ate: ult ? data(ult.data) : '',
          saldo: ult ? dinheiro(ult.saldo, moeda) : '',
        })}
      >
        <line x1={0} x2={larg} y1={zero} y2={zero} className="stroke-borda" strokeWidth={1} vectorEffect="non-scaling-stroke" />
        {pontos.map((p, k) => (
          <g key={p.data}>
            <title>{`${rotuloData(p.data)}: ${t('inicio.grafico.entradas')} ${dinheiro(p.entradas, moeda)}, ${t('inicio.grafico.saidas')} ${dinheiro(p.saidas, moeda)}, ${t('inicio.grafico.saldo')} ${dinheiro(p.saldo, moeda)}${prev(p) !== null ? `, ${t('inicio.grafico.previsto')} ${dinheiro(prev(p)!, moeda)}` : ''}`}</title>
            <rect x={k * 10 + 1} width={3.6} y={Math.min(y(p.entradas), zero)} height={Math.abs(zero - y(p.entradas))} className="fill-verde" />
            <rect x={k * 10 + 5.2} width={3.6} y={Math.min(y(p.saidas), zero)} height={Math.abs(zero - y(p.saidas))} className="fill-perigo" />
          </g>
        ))}
        {pontos.length > 0 && <path d={linha} fill="none" className="stroke-apoio" strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" />}
        {comPrevisto.length > 0 && <path d={comPrevisto.length === 1 ? `${linhaPrevista} h1` : linhaPrevista} data-serie="previsto" fill="none" className="stroke-apoio" strokeWidth={2} strokeDasharray="5 4" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />}
      </svg>
      <div className="mt-1 flex justify-between gap-2 text-xs text-tinta-3">
        <span className="numero">{minimo < 0 ? anunciarOcultos(dinheiro(minimo, moeda)) : pontos[0] ? data(pontos[0].data) : ''}</span>
        <span className="numero">{ult ? data(ult.data) : ''}</span>
      </div>
      <figcaption className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-tinta-2">
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-verde" aria-hidden />
          {t('inicio.grafico.entradas')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-perigo" aria-hidden />
          {t('inicio.grafico.saidas')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-4 bg-apoio" aria-hidden />
          {t('inicio.grafico.saldo')}
        </span>
        {temPrevisto && (
          <span className="flex items-center gap-1.5" data-legenda="previsto">
            <span className="w-4 border-t-2 border-dashed border-apoio" aria-hidden />
            {t('inicio.grafico.previstoLegenda')}
          </span>
        )}
      </figcaption>
      {/* sr-only no contêiner: a tabela ignora a largura de 1px e estouraria a página no celular. */}
      <div className="sr-only">
      <table>
        <caption>{t('inicio.grafico.tabela')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('inicio.grafico.data')}</th>
            <th scope="col">{t('inicio.grafico.entradas')}</th>
            <th scope="col">{t('inicio.grafico.saidas')}</th>
            <th scope="col">{t('inicio.grafico.saldo')}</th>
            {temPrevisto && <th scope="col">{t('inicio.grafico.previsto')}</th>}
          </tr>
        </thead>
        <tbody>
          {pontos.map((p) => (
            <tr key={p.data}>
              <th scope="row">{rotuloData(p.data)}</th>
              <td>{anunciarOcultos(dinheiro(p.entradas, moeda))}</td>
              <td>{anunciarOcultos(dinheiro(p.saidas, moeda))}</td>
              <td>{anunciarOcultos(dinheiro(p.saldo, moeda))}</td>
              {temPrevisto && <td>{prev(p) !== null ? anunciarOcultos(dinheiro(prev(p)!, moeda)) : ''}</td>}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </figure>
  );
}
